import React, { useEffect, useState } from "react";
import "./Translate.css";
import { toast } from "react-toastify";

// components
import TranslateDashboard from "../../components/TranslateDashboard/TranslateDashboard";
import DropdownSelector from "../../components/DropdownSelector/DropdownSelector";
import { TranslateFilePreview } from "../../components/TranslateFilePreview/TranslateFilePreview";
import TaskCenter from "../../components/TaskCenter/TaskCenter";
import feedbackIcon from "../../assets/images/feedback.svg";
import GenericTooltip from "../../components/GenericTooltip/GenericTooltip";

// utility & context
import { useConstants } from "../../constant/constantProvider";
import { TRANSLATE_AI_FILE_SIZE_LIMIT } from "../../constant/globalConstants";
import { FEEDBACK_KEYS } from "../../constant/keyConstants";
import { validateFiles } from "../../utils/validateFiles";
import { useDispatch } from "react-redux";
import { fileToBase64, getFileTypeCategory, getObjectUrlFromBase64, displayInvalidFileErrors } from "../../utils/fileHelper";
import { translateText, translateDocs, translateFeedbackStatus, translateFileStatus, fetchTranslatedFile, fetchTranslationLanguages } from "../../store/apiActions";
import { sanitizeTextInput } from "../../utils/textUtils";
import { useAuth } from "../../hooks/useAuth";
import { useRbac } from "../../hooks/useRbac";
import { RBAC_APPS, TranslationFeatures } from "../../constant/rbacConfig";

// assets
import TRANSLATE_ICON from "../../assets/images/translation.svg";
import DOCS_ICON from "../../assets/images/translate-docs-bi.svg";
import DOCS_ICON_ACTIVE from "../../assets/images/translate-docs.svg";
import TEXT_ICON from "../../assets/images/translate-text-bi.svg";
import TEXT_ICON_ACTIVE from "../../assets/images/translate-text.svg";
import WORK_ITEM from "../../assets/images/work-item.svg";
import DOCX_ICON from "../../assets/icons/word.svg";
import PDF_ICON from "../../assets/icons/pdf.svg";
import like from "../../assets/new-images/like-icon.svg";
import dislike from "../../assets/new-images/dislike-icon.svg";
import likeSelected from "../../assets/new-images/active-like-icon.svg";
import dislikeSelected from "../../assets/new-images/active-dislike-icon.svg";
import copyInactive from "../../assets/new-images/copy-icon.svg";
import copyActive from "../../assets/new-images/active-copy-icon.svg";
import JPEG_ICON from "../../assets/icons/jpeg.svg";

const TranslationAI = () => {
  const dispatch = useDispatch();
  const constants = useConstants();
  const {
    TRANSLATE_AI,
    TASK_CENTER,
    TRANSLATE,
    SELECT_FILE_TO_UPLOAD,
    FILE_INFO,
    SELECT_FILE,
    SELECT_LANGUAGE,
    DOC_ERROR,
    TEXT_ERROR,
    LANGUAGE_ERROR,
    TRANSLATING_DOCUMENT,
    RESET,
    FEEDBACK,
    DISABLE_FEATURE_TOOLTIP,
    COPY_SUCCESS,
    FEEDBACK_SUBMIT_SUCCESS,
    FAILED_TO_COPY
  } = constants;

  const [showTranslateDashboardInfo, setShowTranslateDashboardInfo] =
    useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState("");
  const MAX_CHAR = 4000;
  const MAX_FILE_SIZE = TRANSLATE_AI_FILE_SIZE_LIMIT;
  const allowedFiles = ["pdf", "docx", "jpeg"];
  const [text, setText] = useState("");
  const [translatedText, setTranslatedText] = useState(null);
  const [file, setFile] = useState(null);
  const [fileURL, setFileURL] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [selectedTranslationLanguage, setSelectedTranslationLanguage] = useState(null);
  const [translatedFile, setTranslatedFile] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pollingId, setPollingId] = useState(null);
  const [feedbackStatus, setFeedbackStatus] = useState(null);
  const [showTaskCenter, setShowTaskCenter] = useState(false);

  useEffect(() => {
    return () => {
      if (pollingId) {
        clearInterval(pollingId);
      }
    };
  }, [pollingId]);

  const { username, ugdnId: userUGDN } = useAuth();
  const { hasFeature } = useRbac();

  // Check if translation features are enabled
  const isDocTranslationEnabled = hasFeature(TranslationFeatures.DOC_TRANSLATION, RBAC_APPS.TRANSLATION);
  const isTextTranslationEnabled = hasFeature(TranslationFeatures.TEXT_TRANSLATION, RBAC_APPS.TRANSLATION);

  // Automatically route to docs if text translation is disabled
  useEffect(() => {
    if (!isTextTranslationEnabled && isDocTranslationEnabled) {
      const currentSelected = options.find((opt) => opt.isSelected);
      // Only auto-route if text is currently selected or if nothing is selected
      if (!currentSelected || currentSelected.id === "text") {
        handleOptionSelect("docs");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTextTranslationEnabled, isDocTranslationEnabled]);


  const initialOptions = [
    {
      id: "docs",
      title: "Translate Document",
      subtitle: "Upload PDF, DOCX or JPEG for full document translation.",
      defaultIcon: DOCS_ICON,
      activeIcon: DOCS_ICON_ACTIVE,
      isSelected: false,
    },
    {
      id: "text",
      title: "Translate Text",
      subtitle: "Type or paste text for quick translation in 20+ languages.",
      defaultIcon: TEXT_ICON,
      activeIcon: TEXT_ICON_ACTIVE,
      isSelected: true,
    },
  ];

  const [options, setOptions] = useState(initialOptions);

  const selectedTranslationOption = options.find((opt) => opt.isSelected);
  const [isTranslatingDoc, setIsTranslatingDoc] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);
  const [totalTranslateLanguageList, setTotalTranslateLanguageList] = useState([]);

  // Call translateCheckCall on page load/reload
  useEffect(() => {
    mapTranslationLanguageList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mapTranslationLanguageList = async () => {
    try {
      const response = await dispatch(fetchTranslationLanguages());
      setTotalTranslateLanguageList(response?.languages);
    }
    catch (error) {
      console.error("Error fetching translation languages:", error);
    }
  }

  const handleOptionSelect = (id) => {
    if (selectedTranslationOption?.id === id) return;
    setOptions((prev) =>
      prev.map((opt) =>
        opt.id === id
          ? { ...opt, isSelected: true }
          : { ...opt, isSelected: false }
      )
    );
    reset();
  };

  const getStartedTranlationAI = () => {
    setShowTranslateDashboardInfo(false);
  };

  const openTaskCenter = () => {
    setShowTaskCenter(true);
  };

  const closeTaskCenter = () => {
    setShowTaskCenter(false);
  };

  const processFile = async (uploadedFile) => {
    if (!uploadedFile) return;

    // File size check
    if (uploadedFile.size > MAX_FILE_SIZE) {
      toast.error(`File size exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit: ${uploadedFile.name}`);
      return;
    }

    const { validFiles, invalidFiles } = await validateFiles(
      [uploadedFile],
      allowedFiles
    );

    if (validFiles.length > 0) {
      const file = validFiles[0];
      setFile(file);

      // create a preview URL
      const fileURL = URL.createObjectURL(file);

      setFileURL(fileURL);
    } else {
      displayInvalidFileErrors(invalidFiles, toast, constants);
    }
  };

  const handleFileChange = (e) => {
    processFile(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFile(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleLanguageChange = (language) => {
    setSelectedTranslationLanguage(language);
  };

  const handleTranslateClick = async () => {
    try {
      setIsFileUploading(true);

      if (!selectedTranslationLanguage) {
        toast.error(LANGUAGE_ERROR);
        return;
      }

      if (selectedTranslationOption?.id === "text") {
        setTranslatedText(null);
        setFeedbackStatus(null);
        if (!text.trim()) {
          toast.error(TEXT_ERROR);
          return;
        }

        // Dispatch text translation API
        const response = await dispatch(
          translateText({
            text: sanitizeTextInput(text),
            target_language: selectedTranslationLanguage,
            user_id: username,
            ugdn_id: userUGDN || "",
          })
        );

        if (response?.content) {
          setTranslatedText(response);
          setDetectedLanguage(response?.detected_language);
        }
      }

      else if (selectedTranslationOption?.id === "docs") {
        if (!file) {
          toast.error(DOC_ERROR);
          return;
        }

        const fileExtension = file.name.split(".").pop().toLowerCase();

        if (!allowedFiles.includes(fileExtension)) {
          toast.error("Only PDF, DOC/DOCX and JPEG files are supported");
          return;
        }

        const normalizedExt = fileExtension?.toLowerCase();

        let outputFormat;

        if (normalizedExt === "pdf") {
          outputFormat = "pdf";

        } else if (["jpeg"].includes(normalizedExt)) {
          outputFormat = "jpeg";

        } else if (["doc", "docx"].includes(normalizedExt)) {
          outputFormat = "docx";

        } else {
          throw new Error(`Unsupported file format: ${normalizedExt}`);
        }

        const base64File = await fileToBase64(file);

        const payload = {
          filename: file.name,
          target_language: selectedTranslationLanguage,
          filedata: base64File,
          username,
          ugdn_id: userUGDN || "",
          userteam: "",
          output_format: outputFormat,
        };

        const response = await dispatch(translateDocs(payload));
        setIsTranslatingDoc(true);
        setTranslationProgress(0);

        if (response?.task_id) {
          setTranslatedFile(null);
          const taskId = response.task_id;
          setDetectedLanguage(response?.detected_language);

          const intervalId = setInterval(async () => {
            const statusResponse = await dispatch(translateFileStatus({ taskId }));
            setTranslationProgress(parseFloat(statusResponse.progress_so_far || 0));
            if (statusResponse?.status === "COMPLETED") {
              const file_path = statusResponse?.response_path;
              clearInterval(intervalId);
              const responseFile = await dispatch(fetchTranslatedFile({ file_path }));
              const responseBlobUrl = getObjectUrlFromBase64(
                responseFile?.encoded_filebytes,
                selectedTranslationOption?.id
              );
              setTranslatedFile({
                url: responseBlobUrl,
                name: file_path.split("/").pop()
              });
              setIsTranslatingDoc(false);
              setTranslationProgress(0);
            } else if (statusResponse?.status === "FAILED") {
              clearInterval(intervalId);
              setIsTranslatingDoc(false);
              setTranslationProgress(0);
              const errorMsg =
                statusResponse?.error_msg ||
                statusResponse?.error ||
                "Translation failed. Please try again.";
              toast.error(errorMsg);
            }
          }, 10000);

          setPollingId(intervalId);
        } else {
          toast.error("Failed to start translation.");
        }
      }
    } catch (err) {
      console.error("Translation error:", err);
      toast.error("Something went wrong. Please try again.");
      setIsTranslatingDoc(false);
    } finally {
      setIsFileUploading(false);
    }
  };

  const handleOpenForm = () => {
    window.open(
      "https://forms.office.com/r/1670KjDg4P",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleFeedback = async (text, action) => {
    // prevent duplicate same action
    if (feedbackStatus === action) return;

    const payload = {
      task_id: text?.task_id,
      user_id: username,
      feedback_sign: action,
      feedback_comment: "N/A",
    };

    const response = await dispatch(translateFeedbackStatus(payload));

    if (response) {
      toast.info(FEEDBACK_SUBMIT_SUCCESS || "Feedback submitted successfully");
      setFeedbackStatus(action);
    }
  };


  const handleCopyClick = async (translate) => {
    const text = translate?.content;

    if (!text) return;

    try {
      const clipboardItem = new ClipboardItem({
        "text/plain": new Blob([text], { type: "text/plain" }),
        "text/html": new Blob([`<div>${text}</div>`], { type: "text/html" }),
      });

      await navigator.clipboard.write([clipboardItem]);

      setCopied(true);
      toast.info(COPY_SUCCESS || "Copied to clipboard!", { autoClose: 1000 });
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast.error(FAILED_TO_COPY || "Failed to copy.");
    }
  };

  const reset = () => {
    setText("");
    setTranslatedText(null);
    setDetectedLanguage("");
    setFile(null);
    setDragOver(false);
    setSelectedTranslationLanguage(null);
    setTranslatedFile(null);
    setFeedbackStatus(null);
    setIsFileUploading(false);
    setIsTranslatingDoc(false);
    setTranslationProgress(0);
    if (pollingId) {
      clearInterval(pollingId);
      setPollingId(null);
    }
  };

  const isResetDisabled =
    !text.trim() &&
    !file &&
    !selectedTranslationLanguage &&
    !translatedText &&
    !translatedFile &&
    !isTranslatingDoc;

  const isTranslatorDisabled =
    isFileUploading ||
    isTranslatingDoc ||
    !selectedTranslationLanguage ||
    (selectedTranslationOption?.id === "text" && !text.trim()) ||
    (selectedTranslationOption?.id === "docs" && !file);

  return (
    <div className="translate-container">
      {showTranslateDashboardInfo ? (
        <div className="translate-dashboard-section">
          <TranslateDashboard primaryButtonClick={getStartedTranlationAI} />
        </div>
      ) : showTaskCenter ? (
        <TaskCenter onClose={closeTaskCenter} />
      ) : (
        <div className="translate-inner-container">
          <div className="translate-heading-section">
            <img src={TRANSLATE_ICON} alt="Translate" />
            <p className="translate-text">{TRANSLATE_AI}</p>
          </div>
          <div className="translate-options">
            {options.map((option) => {
              const isDisabled =
                (option.id === "docs" && !isDocTranslationEnabled) ||
                (option.id === "text" && !isTextTranslationEnabled);

              const buttonContent = (
                <button
                  key={option.id}
                  onClick={() => handleOptionSelect(option.id)}
                  disabled={isDisabled}
                  className={`option-card ${option.isSelected ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
                >
                  <div className="option-header">
                    <img
                      src={
                        option.isSelected ? option.activeIcon : option.defaultIcon
                      }
                      alt={option.title}
                      className="option-icon"
                    />
                    <span className="option-title">{option.title}</span>
                  </div>
                  <div className="option-subtitle">{option.subtitle}</div>
                </button>
              );

              return isDisabled ? (
                <GenericTooltip key={option.id} tooltipText={DISABLE_FEATURE_TOOLTIP}>
                  {buttonContent}
                </GenericTooltip>
              ) : (
                buttonContent
              );
            })}
          </div>
          {selectedTranslationOption?.id === "docs" && (
            <div className="task-center-button-section">
              <button
                className="button-style task-center-button"
                type="button"
                onClick={openTaskCenter}
              >
                <img src={WORK_ITEM} alt="task" />
                <span>{TASK_CENTER}</span>
              </button>
            </div>
          )}
          <div className="translate-zone-main-section">
            <div className="language-detector-section">
              <div className="detect-language-section">
                {selectedTranslationOption?.id === "text" &&
                  <input
                    type="text"
                    value={detectedLanguage || "Detect Language"}
                    readOnly
                  />
                }
              </div>
              <div className="language-select-section">
                <DropdownSelector
                  items={totalTranslateLanguageList}
                  selected={selectedTranslationLanguage}
                  onChange={handleLanguageChange}
                  placeholder={{ name: SELECT_LANGUAGE, image: null }}
                  itemConfig={{
                    getLabel: (item) => item,
                    getValue: (item) => item,
                  }}
                  type="selected"
                />
              </div>
            </div>
            {selectedTranslationOption?.id === "docs" ? (
              <div className="translate-docs-section translate-part-section">
                <div className="block-part">
                  <div className={`doc-area-wrapper ${dragOver ? "drag-over" : ""}`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}>
                    {file ? (
                      <div className="file-preview-section">
                        <TranslateFilePreview fileURL={fileURL} fileType={getFileTypeCategory(file)} fileName={file.name} enabledActionButton={false} isConversion={false} />
                      </div>
                    ) : (
                      <div className="upload-files-section">
                        <div className="file-image-section">
                          <img src={DOCX_ICON} alt="media" />
                          <img src={PDF_ICON} alt="media" />
                          <img src={JPEG_ICON} alt="media" />
                        </div>
                        <div className="file-upload-head-text">{SELECT_FILE_TO_UPLOAD}</div>
                        <div className="file-upload-info-text">{FILE_INFO}</div>
                        <label className="button-style outline-button select-file-btn">
                          {SELECT_FILE}
                          <input
                            type="file"
                            accept=".pdf,.docx,.jpeg"
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </div>
                <div className="line"></div>
                <div className="block-part">
                  <div className="doc-area-wrapper">
                    {isTranslatingDoc ? (
                      <div className="file-preview-section">
                        <div className="translation-progress-section">
                          <span className="translate-document">{TRANSLATING_DOCUMENT}</span>
                          <div className="progress-container">
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{ width: `${Math.min(100, Math.max(0, Math.round(translationProgress)))}%` }}
                              ></div>
                            </div>
                            <span className="progress-text">{Math.round(translationProgress)}%</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      translatedFile && (
                        <div className="file-preview-section">
                          <TranslateFilePreview
                            fileURL={translatedFile?.url}
                            fileType={getFileTypeCategory(file)}
                            fileName={translatedFile?.name || "Translated Document"}
                            isConversion={true}
                          />
                        </div>
                      )
                    )}
                  </div>
                  {translatedFile && !isTranslatingDoc && (
                    <button
                      type="button"
                      className="feedback-document button-style"
                      onClick={handleOpenForm}
                    >
                      <img
                        src={feedbackIcon}
                        alt="feedback"
                        className="feedback-icon"
                      />
                      <span>{FEEDBACK}</span>
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="translate-text-section translate-part-section">
                <div className="block-part">
                  <div className="textarea-wrapper">
                    <textarea
                      className="translator-textarea"
                      placeholder="Paste or type your text (Max 4000 characters)."
                      value={sanitizeTextInput(text)}
                      maxLength={MAX_CHAR}
                      onChange={(e) => setText(e.target.value)}
                    />
                    <div className="char-count">Characters used:  {text.length} of {MAX_CHAR}
                    </div>
                  </div>
                </div>
                <div className="line"></div>
                <div className="block-part">
                  <div className="textarea-wrapper">
                    <div
                      className={
                        translatedText?.content
                          ? "translation-output-box text-value"
                          : "translation-output-box text-placeholder"
                      }
                    >
                      {translatedText?.content || "Your translation will appear here."}
                    </div>
                  </div>
                  {translatedText?.content && !isFileUploading &&
                    <div className="translate-feedback-section">
                      <button
                        className="button-style"
                        disabled={feedbackStatus === FEEDBACK_KEYS.LIKE}
                        onClick={() => handleFeedback(translatedText, FEEDBACK_KEYS.LIKE)}
                      >
                        <img
                          src={feedbackStatus === FEEDBACK_KEYS.LIKE ? likeSelected : like}
                          className="feedback-image"
                          alt="like icon"
                        />
                      </button>

                      <button
                        className="button-style"
                        disabled={feedbackStatus === FEEDBACK_KEYS.DISLIKE}
                        onClick={() => handleFeedback(translatedText, FEEDBACK_KEYS.DISLIKE)}
                      >
                        <img
                          src={feedbackStatus === FEEDBACK_KEYS.DISLIKE ? dislikeSelected : dislike}
                          className="feedback-image"
                          alt="dislike icon"
                        />
                      </button>

                      <button
                        className="button-style"
                        disabled={copied}
                        onClick={() => handleCopyClick(translatedText)}
                      >
                        <img
                          src={copied ? copyActive : copyInactive}
                          className="feedback-image"
                          alt="copy icon"
                        />
                      </button>
                    </div>
                  }
                </div>
              </div>
            )}
          </div>
          <div className="translate-button-section">
            <button
              className={`primary-blue-button primary-start-button button-style ${isTranslatorDisabled ? "disabled-button" : ""}`}
              type="button"
              disabled={isTranslatorDisabled}
              onClick={handleTranslateClick}
            >
              {TRANSLATE}
            </button>
            <button
              className={`outline-button button-style ${isResetDisabled ? "disabled-button" : ""
                }`}
              type="button"
              disabled={isResetDisabled}
              onClick={reset}
            >
              {RESET}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranslationAI;
